import React from 'react';
import { Box } from '@mui/material';
import {
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isBefore,
  isSameDay,
  isSameMonth,
  isSunday,
  isToday as isTodayFn,
  startOfMonth,
  startOfToday,
  startOfWeek,
} from 'date-fns';
import { useMobileMonthGridStyles } from './MobileMonthGrid.styles';

const WEEK_DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const SUNDAY_COLOR = '#F76E63';
const WEEKDAY_COLOR = '#7B8BAD';
const OUTSIDE_COLOR = '#7B8BAD';
const DEFAULT_NUMBER_COLOR = '#383C44';
const PRIMARY_BLUE = '#4E6DF5';

interface MobileMonthGridProps {
  currentDate: Date;
  eventsByDate: Map<string, any[]>;
  selectedDate: Date | null;
  onDayClick: (date: Date) => void;
}

interface DayCellProps {
  date: Date;
  isOutsideMonth: boolean;
  isSelected: boolean;
  isToday: boolean;
  isPastDay: boolean;
  hasEvents: boolean;
  isSundayDay: boolean;
  onClick: (date: Date) => void;
}

const DayCell: React.FC<DayCellProps> = ({
  date,
  isOutsideMonth,
  isSelected,
  isToday,
  isPastDay,
  hasEvents,
  isSundayDay,
  onClick,
}) => {
  const { classes, cx } = useMobileMonthGridStyles();

  const numberColor = (() => {
    if (isSelected) return '#fff';
    if (isToday) return PRIMARY_BLUE;
    if (isOutsideMonth) return OUTSIDE_COLOR;
    if (isSundayDay) return SUNDAY_COLOR;
    return DEFAULT_NUMBER_COLOR;
  })();

  const dotColor = (() => {
    if (isSelected) return '#fff';
    if (isPastDay) return WEEKDAY_COLOR;
    return PRIMARY_BLUE;
  })();

  const baseDotStyle: React.CSSProperties = {
    width: 8,
    height: 8,
    borderRadius: '50%',
    marginTop: 0.8,
    visibility: hasEvents ? 'visible' : 'hidden',
  };

  const dotStyle: React.CSSProperties =
    hasEvents && !isSelected && isOutsideMonth
      ? { ...baseDotStyle, border: `1px solid ${PRIMARY_BLUE}`, backgroundColor: 'transparent' }
      : { ...baseDotStyle, backgroundColor: dotColor };

  const handleClick = () => {
    if (hasEvents) onClick(date);
  };

  const inner = (
    <>
      <span className={classes.dayNumber} style={{ color: numberColor }}>
        {format(date, 'd')}
      </span>
      <Box component="span" sx={dotStyle} />
    </>
  );

  return (
    <Box
      className={cx(classes.dayCell, hasEvents && classes.dayCellClickable)}
      onClick={handleClick}
      aria-label={format(date, 'MMMM d, yyyy')}
    >
      {isSelected ? <Box className={classes.selectedWrapper}>{inner}</Box> : inner}
    </Box>
  );
};

const MobileMonthGrid: React.FC<MobileMonthGridProps> = ({ currentDate, eventsByDate, selectedDate, onDayClick }) => {
  const { classes } = useMobileMonthGridStyles();

  const days = eachDayOfInterval({
    start: startOfWeek(startOfMonth(currentDate), { weekStartsOn: 0 }),
    end: endOfWeek(endOfMonth(currentDate), { weekStartsOn: 0 }),
  });

  return (
    <Box>
      {/* Week day header row */}
      <Box className={classes.grid}>
        {WEEK_DAY_LABELS.map((label, idx) => (
          <Box key={label} className={classes.headerCell} style={{ color: idx === 0 ? SUNDAY_COLOR : WEEKDAY_COLOR }}>
            {label}
          </Box>
        ))}
      </Box>

      {/* Day cells */}
      <Box className={classes.grid}>
        {days.map((date) => {
          const dayKey = format(date, 'yyyy-MM-dd');
          const hasEvents = (eventsByDate.get(dayKey)?.length ?? 0) > 0;
          const isOutsideMonth = !isSameMonth(date, currentDate);
          const isSelected = selectedDate ? isSameDay(date, selectedDate) : false;
          const isSundayDay = isSunday(date);
          const isToday = isTodayFn(date);
          const isPastDay = isBefore(date, startOfToday());

          return (
            <DayCell
              key={dayKey}
              date={date}
              isOutsideMonth={isOutsideMonth}
              isSelected={isSelected}
              isToday={isToday}
              isPastDay={isPastDay}
              hasEvents={hasEvents}
              isSundayDay={isSundayDay}
              onClick={onDayClick}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default MobileMonthGrid;
